/**
 * Preorder scheduling utilities for Capricho de Canela (restaurant_id = 2).
 * Orders: Monday–Friday
 * Deliveries: Saturday only, 8:00 a.m. – 4:00 p.m.
 */

// Restaurant IDs that use preorder mode
const PREORDER_RESTAURANT_IDS = ['2']

export function isPreorderRestaurant(
  restaurantId: string | undefined,
): boolean {
  if (!restaurantId) return false
  return PREORDER_RESTAURANT_IDS.includes(restaurantId)
}

/** Time slots for Saturday delivery — HARDCODED FALLBACK */
export const DELIVERY_TIME_SLOTS = [
  { id: '08-10', label: '8:00 a.m. – 10:00 a.m.' },
  { id: '10-12', label: '10:00 a.m. – 12:00 p.m.' },
  { id: '12-14', label: '12:00 p.m. – 2:00 p.m.' },
  { id: '14-16', label: '2:00 p.m. – 4:00 p.m.' },
]

/** Dynamic time slot from Supabase (public.delivery_time_slots) */
export interface DeliveryTimeSlot {
  id: string
  restaurant_id: number
  label: string
  start_time: string
  end_time: string
  max_orders: number
  sort_order: number
  is_active: boolean
}

/** Slot with availability info */
export interface TimeSlotWithAvailability extends DeliveryTimeSlot {
  currentOrders: number
  available: boolean
}

/**
 * Fetch active delivery time slots from public.delivery_time_slots.
 * Falls back to hardcoded DELIVERY_TIME_SLOTS on error.
 */
export async function fetchDeliveryTimeSlots(
  restaurantId: string,
): Promise<DeliveryTimeSlot[]> {
  try {
    const { supabase } = await import('./supabase')
    const { data, error } = await supabase
      .from('delivery_time_slots')
      .select(
        'id, restaurant_id, label, start_time, end_time, max_orders, sort_order, is_active',
      )
      .eq('restaurant_id', restaurantId)
      .eq('is_active', true)
      .order('sort_order', { ascending: true })

    if (error || !data || data.length === 0) {
      console.warn('[NOIZA][preorder] No dynamic slots found, using fallback')
      const rid = Number(restaurantId) || 2
      return DELIVERY_TIME_SLOTS.map((s, i) => ({
        id: s.id,
        restaurant_id: rid,
        label: s.label,
        start_time: s.id.split('-')[0] + ':00',
        end_time: s.id.split('-')[1] + ':00',
        max_orders: 20,
        sort_order: i,
        is_active: true,
      }))
    }

    return data
  } catch {
    const rid = Number(restaurantId) || 2
    return DELIVERY_TIME_SLOTS.map((s, i) => ({
      id: s.id,
      restaurant_id: rid,
      label: s.label,
      start_time: s.id.split('-')[0] + ':00',
      end_time: s.id.split('-')[1] + ':00',
      max_orders: 20,
      sort_order: i,
      is_active: true,
    }))
  }
}

/**
 * Check availability for each time slot on a given delivery date.
 * Uses each slot's own max_orders from delivery_time_slots table.
 */
export async function checkTimeSlotsAvailability(
  restaurantId: string,
  deliveryDate: string,
  slots: DeliveryTimeSlot[],
): Promise<TimeSlotWithAvailability[]> {
  try {
    const { supabase } = await import('./supabase')

    const results: TimeSlotWithAvailability[] = await Promise.all(
      slots.map(async (slot) => {
        const { count, error } = await supabase
          .from('orders')
          .select('id', { count: 'exact', head: true })
          .eq('restaurant_id', restaurantId)
          .eq('delivery_date', deliveryDate)
          .eq('delivery_time_slot', slot.label)
          .neq('status', 'cancelled')

        const currentOrders = error ? 0 : (count ?? 0)

        return {
          ...slot,
          currentOrders,
          available: currentOrders < slot.max_orders,
        }
      }),
    )

    console.log(
      `[NOIZA][preorder] Slot availability for ${deliveryDate}:`,
      results.map(
        (r) =>
          `${r.label}: ${r.currentOrders}/${r.max_orders} ${r.available ? '✓' : '✗'}`,
      ),
    )

    return results
  } catch (err) {
    console.error('[NOIZA][preorder] Slot availability check error:', err)
    // Fail open — mark all as available
    return slots.map((s) => ({ ...s, currentOrders: 0, available: true }))
  }
}

/** Check if today is within the ordering window (Monday–Friday) */
export function isOrderingWindowOpen(): boolean {
  const now = new Date()
  const day = now.getDay() // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat
  return day >= 1 && day <= 5
}

/** Get the day name in Spanish */
function getDayName(date: Date): string {
  const days = [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
  ]
  return days[date.getDay()]
}

/** Get the month name in Spanish */
function getMonthName(date: Date): string {
  const months = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ]
  return months[date.getMonth()]
}

/** Format a date as "Sábado 25 de enero" */
export function formatDeliveryDate(date: Date): string {
  return `${getDayName(date)} ${date.getDate()} de ${getMonthName(date)}`
}

/** Get the next N Saturdays from today */
export function getUpcomingSaturdays(count: number = 3): Date[] {
  const saturdays: Date[] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  let current = new Date(today)

  // Find next Saturday
  const daysUntilSaturday = (6 - current.getDay() + 7) % 7
  if (daysUntilSaturday === 0) {
    // Today is Saturday — if ordering window is closed, skip to next week
    current.setDate(current.getDate() + 7)
  } else {
    current.setDate(current.getDate() + daysUntilSaturday)
  }

  for (let i = 0; i < count; i++) {
    saturdays.push(new Date(current))
    current.setDate(current.getDate() + 7)
  }

  return saturdays
}

/**
 * Get upcoming delivery dates (Saturdays + Sundays) starting from March 28, 2026.
 * March 23 (Sun) and March 29 (Sun) are NOT available.
 */
export function getUpcomingDeliveryDates(count: number = 6): Date[] {
  const dates: Date[] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Earliest allowed date: Saturday March 28, 2026
  const earliest = new Date(2026, 2, 28) // month is 0-indexed
  earliest.setHours(0, 0, 0, 0)

  // Blocked dates (Sunday March 29 is not available)
  const blocked = new Set(['2026-03-29'])

  let current = new Date(Math.max(today.getTime(), earliest.getTime()))
  // If current is before earliest, start from earliest
  if (current < earliest) current = new Date(earliest)

  // Find next Saturday or Sunday
  while (current.getDay() !== 0 && current.getDay() !== 6) {
    current.setDate(current.getDate() + 1)
  }

  // If today IS a Sat/Sun but it's already past, skip to next
  if (current.getTime() === today.getTime() && current < earliest) {
    current.setDate(current.getDate() + 1)
    while (current.getDay() !== 0 && current.getDay() !== 6) {
      current.setDate(current.getDate() + 1)
    }
  }

  while (dates.length < count) {
    const dateStr = toDateString(current)
    const isSatOrSun = current.getDay() === 0 || current.getDay() === 6
    const isBlocked = blocked.has(dateStr)
    const isBeforeEarliest = current < earliest

    if (isSatOrSun && !isBlocked && !isBeforeEarliest) {
      dates.push(new Date(current))
    }

    // Move to next day, then find next Sat/Sun
    current.setDate(current.getDate() + 1)
    while (current.getDay() !== 0 && current.getDay() !== 6) {
      current.setDate(current.getDate() + 1)
    }
  }

  return dates
}

/** Delivery hours for Capricho (8am - 4pm, hourly) */
export const CAPRICHO_DELIVERY_HOURS = [
  '8:00 a.m.',
  '9:00 a.m.',
  '10:00 a.m.',
  '11:00 a.m.',
  '12:00 p.m.',
  '1:00 p.m.',
  '2:00 p.m.',
  '3:00 p.m.',
  '4:00 p.m.',
]

/** Format date as ISO string (YYYY-MM-DD) for form values */
export function toDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Get a friendly message about the ordering window */
export function getOrderingWindowMessage(): {
  isOpen: boolean
  title: string
  message: string
} {
  const now = new Date()
  const day = now.getDay()

  if (day >= 1 && day <= 5) {
    return {
      isOpen: true,
      title: '📦 Pedido para este sábado',
      message:
        'Los pedidos se reciben de lunes a viernes. Tu pedido será entregado este sábado.',
    }
  }

  if (day === 6) {
    return {
      isOpen: false,
      title: '🚚 Hoy es día de entregas',
      message:
        'Hoy sábado estamos entregando pedidos. Los nuevos pedidos se reciben de lunes a viernes para el próximo sábado.',
    }
  }

  // Sunday
  return {
    isOpen: false,
    title: '😴 Descansamos hoy',
    message:
      'Los pedidos se reciben de lunes a viernes. ¡Vuelve mañana para hacer tu pedido para el próximo sábado!',
  }
}

// ── Capacity limit for preorder restaurants ──

/** Hardcoded fallback if restaurant_settings is not configured */
const DEFAULT_MAX_ORDERS_PER_DELIVERY_DAY = 80

interface RestaurantCapacitySettings {
  maxOrdersPerDay: number
}

/**
 * Fetch capacity settings from public.restaurant_settings.
 * Per-slot limits come from delivery_time_slots.max_orders instead.
 * Falls back to default if not found or on error.
 */
export async function getRestaurantCapacitySettings(
  restaurantId: string,
): Promise<RestaurantCapacitySettings> {
  try {
    const { supabase } = await import('./supabase')
    const { data, error } = await supabase
      .from('restaurant_settings')
      .select('max_orders_per_delivery_day')
      .eq('restaurant_id', restaurantId)
      .single()

    if (error || !data) {
      return { maxOrdersPerDay: DEFAULT_MAX_ORDERS_PER_DELIVERY_DAY }
    }

    return {
      maxOrdersPerDay:
        data.max_orders_per_delivery_day ?? DEFAULT_MAX_ORDERS_PER_DELIVERY_DAY,
    }
  } catch {
    return { maxOrdersPerDay: DEFAULT_MAX_ORDERS_PER_DELIVERY_DAY }
  }
}

/**
 * Check if a given Saturday still has capacity for new preorders.
 * 1. Reads max_orders_per_delivery_day from restaurant_settings
 * 2. Counts orders by delivery_date
 * 3. Returns available: true/false
 */
export async function checkPreorderCapacity(
  restaurantId: string,
  deliveryDate: string,
): Promise<{ available: boolean }> {
  try {
    const { supabase } = await import('./supabase')

    // 1. Get the configured limit
    const settings = await getRestaurantCapacitySettings(restaurantId)

    // 2. Count orders with delivery_date column
    const { count, error } = await supabase
      .from('orders')
      .select('id', { count: 'exact', head: true })
      .eq('restaurant_id', restaurantId)
      .eq('delivery_date', deliveryDate)
      .neq('status', 'cancelled')

    if (error) {
      console.error('[NOIZA][preorder] Capacity check error:', error)
      return { available: true }
    }

    const totalOrders = count ?? 0
    console.log(
      `[NOIZA][preorder] Day capacity: ${totalOrders}/${settings.maxOrdersPerDay} for ${deliveryDate}`,
    )
    return { available: totalOrders < settings.maxOrdersPerDay }
  } catch (err) {
    console.error('[NOIZA][preorder] Capacity check exception:', err)
    return { available: true }
  }
}

/** Message shown when capacity is full */
export const CAPACITY_FULL_MESSAGE =
  'Cupos agotados para esta entrega. Te invitamos a ordenar para la próxima semana.'
